<script lang="ts">
	import { onMount } from 'svelte';
	import { formatNumberWithLocale } from '../../helper/Parser';
	import { locale } from 'svelte-i18n';
	import { handleStoreError } from '../../stores/GeneralStore';

	export let id: string | null = null;
	export let value: number | null = null;
	export let focusOnStart: boolean = false;
	export let placeholder: string | null = null;
	export let propertyName: string;
	export let positionDesign = false;
	export let disabled = false;
	export let rightAlign: boolean = false;
	export let min: number = -2147483648;
	export let max: number = 2147483647;
	export let nullModus: boolean = false;
	export let callFunction: ((object: any) => Promise<any>) | null = null;
	export let errors: any = {};
	export let tabSwitcher: boolean = false;
	export let wrappInOptionalParameter: boolean = false;

	//Adjust the value at start to match min and max
	if (min != null && (value || 0) < min) {
		if (nullModus && value == null) {
			value = null;
		} else {
			value = min;
		}
	} else if (max != null && (value || 0) > max) {
		value = max;
	}

	//Internal used value.
	$: textValue = value != null ? formatNumberWithLocale(value || 0) : nullModus ? '' : '0';

	let swissRegexMatch = new RegExp("^-?([0-9’']?)*$");

	let euRegexMatch = new RegExp('^-?([0-9\\.]?)*$');

	let element: HTMLInputElement | HTMLTextAreaElement;

	onMount(() => {
		if (focusOnStart) {
			element.select();
		}
	});

	function handleBeforeInput(event: InputEvent) {
		const target = event.target as HTMLInputElement | HTMLTextAreaElement;

		//Build the next value
		const nextVal =
			target.value.substring(0, target.selectionStart || 0) +
			(event.data ?? '') +
			target.value.substring(target.selectionEnd || 0);

		let usedRegex = swissRegexMatch;

		/*
		if (!$locale?.endsWith('CH')) {
			usedRegex = euRegexMatch;
		}
	    */

		//Check if we do match the number
		if (nextVal.match(usedRegex) == null) {
			event.preventDefault();
			return;
		}
	}

	async function handleOnChange(event: CustomEvent) {
		const target = event.target as HTMLInputElement | HTMLTextAreaElement;

		if (callFunction) {
			try {
				let localValue = nullModus ? null : 0;

				//Map it to a number
				if (target.value != '') {
					localValue = parseValue(target.value);
				}

				//We must set it to the event value.
				if (min != null && (localValue || 0) < min) {
					if (nullModus && localValue == null) {
						localValue = null;
					} else {
						localValue = min;
					}
				} else if (max != null && (localValue || 0) > max) {
					localValue = max;
				}

				const dynamicObject: any = {};
				//Check if we should wrapp the property in an optional
				if (wrappInOptionalParameter) {
					dynamicObject[propertyName] = { value: localValue };
				} else {
					dynamicObject[propertyName] = localValue;
				}

				await callFunction(dynamicObject);

				//The reactivity is stuck at this point, so we have to help it
				textValue = '';
				//We need to reset the text value incase the parsed text apearse to be the same
				textValue = localValue == null ? '' : formatNumberWithLocale(localValue);

				//Map it to input
				value = localValue;
			} catch (error) {
				//Check if we have an error type
				handleStoreError('general.patch_failed', error);

				//We must reset the value
				target.value = formatNumberWithLocale(value || 0);
			}
		} else {
			try {
				let localValue = nullModus ? null : 0;

				//Map it to a number
				if (target.value != '') {
					localValue = parseValue(target.value);
				}

				//We can set it to the display value.
				if (min != null && (localValue || 0) < min) {
					//Because the values are already the same, the input string does not match
					if (value == min) {
						//The reactivity is stuck at this point, so we have to help it
						textValue = '';
						//Set the right value back
						textValue = localValue == null ? '' : formatNumberWithLocale(value);
					} else {
						value = min;
					}
				} else if (max != null && (localValue || 0) > max) {
					//Because the values are already the same, the input string does not match
					if (value == max) {
						//The reactivity is stuck at this point, so we have to help it
						textValue = '';
						//Set the right value back
						textValue = localValue == null ? '' : formatNumberWithLocale(value);
					} else {
						value = max;
					}
				} else {
					value = localValue;
				}
			} catch (error) {
				value = nullModus ? null : 0;
			}
		}
	}

	function parseValue(textValue: string): number {
		return parseFloat(textValue.replaceAll(/[^\d-]/g, ''));

		/*
		if ($locale?.endsWith('CH')) {
			return parseFloat(textValue.replaceAll(/[^\d-]/g, ''));
		} else {
			return parseFloat(textValue.replaceAll('.', ''));
		}
		*/
	}

	function handleKeyDown(event: KeyboardEvent) {
		if (event.key == 'Enter') {
			element.blur();
		}
	}
</script>

<div class:positionDesign>
	{#if positionDesign}
	
		<textarea
			{id}
			data-testid={propertyName}
			autocomplete="off"
			rows="1"
			{disabled}
			class:switcher={tabSwitcher}
			class:text-right={rightAlign}
			on:beforeinput={handleBeforeInput}
			on:change={handleOnChange}
			on:change
			on:blur
			on:focus={() => element.select()}
			bind:this={element}
			value={textValue}
			on:keydown={handleKeyDown}
			{placeholder}
		></textarea>
	{:else}
		<input
			{id}
			data-testid={propertyName}
			autocomplete="off"
			{disabled}
			class:switcher={tabSwitcher}
			class:text-right={rightAlign}
			on:beforeinput={handleBeforeInput}
			on:change={handleOnChange}
			on:change
			on:blur
			on:focus={() => element.select()}
			bind:this={element}
			value={textValue}
			on:keydown={handleKeyDown}
			{placeholder}
		/>
	{/if}

	{#if errors[propertyName]}
		<p class="text-red dark:text-orange text-sm">{errors[propertyName]}</p>
	{/if}
</div>
